import type { ConfigNode } from '../types/config.types'

export function findConfigNodeById(nodes: ConfigNode[], id: string): ConfigNode | null {
  for (const n of nodes) {
    if (n.id === id) return n
    if (n.children?.length) {
      const f = findConfigNodeById(n.children, id)
      if (f) return f
    }
  }
  return null
}

/** Same idea as backend _generate_table_name from entity display name */
export function tableNameFromEntityName(name: string): string {
  const cleaned = (name || '')
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .replace(/\s+/g, '_')
    .toLowerCase()
  return cleaned || ''
}

export type EntityTableEntry = { name?: string; table_name?: string }

/**
 * Canonical SQL table_name for metadata/data APIs.
 * Prefer designer entities registry (object name → table_name), not URL slug / derived guess.
 */
export async function resolveEntityTableKey(
  connectionId: string,
  objectName?: string | null,
  routeTableHint?: string | null,
): Promise<string | null> {
  const hint = routeTableHint?.trim() || null
  const on = objectName?.trim() || null

  let entries: EntityTableEntry[] = []
  try {
    const { ApiService } = await import('@/utils/api')
    const data = await ApiService.get<{
      catalogs?: EntityTableEntry[]
      documents?: EntityTableEntry[]
    }>('/v1/designer/entities', {
      params: { connection_id: connectionId, include_excluded: true },
    })
    entries = [...(data.catalogs || []), ...(data.documents || [])]
  } catch {
    if (on) return tableNameFromEntityName(on) || hint
    return hint
  }

  if (on) {
    const byName = entries.find((e) => (e.name || '').trim() === on)
    if (byName?.table_name?.trim()) return byName.table_name.trim()
  }

  if (hint) {
    const hl = hint.toLowerCase()
    const byTable = entries.find((e) => (e.table_name || '').trim().toLowerCase() === hl)
    if (byTable?.table_name?.trim()) return byTable.table_name.trim()
  }

  if (on) {
    const derived = tableNameFromEntityName(on)
    if (derived) {
      const byDerived = entries.find(
        (e) => (e.table_name || '').trim().toLowerCase() === derived,
      )
      if (byDerived?.table_name?.trim()) return byDerived.table_name.trim()
    }
    return derived || hint
  }

  return hint
}

/** Node types that represent concrete entities with metadata. */
const ENTITY_TYPES = new Set<string>([
  'catalog',
  'directory',
  'document',
  'register',
  'informationRegister',
  'InformationRegister',
  'accumulationRegister',
  'AccumulationRegister',
  'register_information',
  'register_accumulation',
  'enumeration',
  'enum',
  'report',
  'dataProcessor',
])

export function isEntityNode(node: ConfigNode | null | undefined): boolean {
  if (!node) return false
  return ENTITY_TYPES.has(node.type)
}

/**
 * Key for GET /entity/{table}/metadata: node table_name, else derived from name,
 * else nodeId (may be invalid for the API → 404).
 * Returns null when the node is not an entity (caller should skip the API call).
 */
export function resolveEntityMetadataTableKey(nodeId: string, tree: ConfigNode[] | undefined): string | null {
  if (!tree?.length) return nodeId
  const n = findConfigNodeById(tree, nodeId)
  if (n && !isEntityNode(n)) return null
  const tn = n?.table_name?.trim()
  if (tn) return tn
  if (n?.name) {
    const derived = tableNameFromEntityName(n.name)
    if (derived) return derived
  }
  return nodeId
}

/** Configuration object name for GET /entity/{table}/metadata?object_name= (shared SQL table). */
export function resolveEntityMetadataObjectName(
  nodeId: string,
  tree: ConfigNode[] | undefined,
): string | null {
  if (!tree?.length) return null
  const n = findConfigNodeById(tree, nodeId)
  if (n && !isEntityNode(n)) return null
  const name = (n?.name || '').trim()
  return name || null
}
